/**
 * Pointer-driven 3D tilt for product cards.
 *
 * A port of Motion's tilt-card example (https://motion.dev/examples/js-tilt-card): same
 * geometry — 15deg at the edges, 500px of perspective, a 10px push back while hovered —
 * driven by a small spring integrator rather than by pulling Motion into the theme.
 *
 * Two deliberate departures from a literal port, both to keep the effect additive:
 *
 * 1. The transform lands on `.product-card__content`, never on `.product-card`. The theme's
 *    `card_hover_effect` setting already writes `transform` on `.product-card:hover` from
 *    card-hover-effect-styles.liquid, and an inline transform on the same element would win
 *    and silently drop the merchant's chosen lift/scale. On the child the two compose.
 *
 * 2. Perspective is a `perspective()` function inside the card's own transform — what Motion
 *    calls `transformPerspective` — rather than the `perspective` property on the parent. The
 *    property would make every card a containing block and a stacking context, which traps
 *    the popovers and quick-add surfaces that are meant to escape the card.
 *
 * Pointer tracking is delegated from the document rather than bound per card, so cards that
 * arrive after load — facet filtering morphs them in, the theme editor re-renders them — are
 * picked up with no re-initialisation.
 *
 * Product cards opt in by their theme class names. Anything else — the shop page's category
 * tiles, say — opts in with `data-tilt-card` on the outer element and `data-tilt-content` on
 * the one child that should receive the transform, and gets identical behaviour.
 */

/** Elements the pointer is tracked over. */
const CARD_SELECTOR = '.product-card, [data-tilt-card]';
/** The direct child of a card that actually carries the transform. */
const CONTENT_SELECTOR = ':scope > .product-card__content, :scope > [data-tilt-content]';

/** Degrees of rotation at the very edge of the card. */
const MAX_TILT = 15;
/** Push the card away from the viewer while hovered, in px. */
const PUSH_Z = -10;

/** Spring constants: settles in ~0.3s with a trace of overshoot. */
const STIFFNESS = 260;
const DAMPING = 26;
const MASS = 1;

/** A value this close to its target, moving this slowly, is treated as at rest. */
const REST_OFFSET = 0.01;
const REST_VELOCITY = 0.5;

/**
 * Clamps the integrator step. Without it a dropped frame or a backgrounded tab hands the
 * spring a multi-second dt and it explodes rather than settles.
 */
const MAX_STEP = 1 / 30;

const ROTATE_X = 0;
const ROTATE_Y = 1;
const TRANSLATE_Z = 2;

/**
 * Cards currently holding a non-default transform. A card stays here while it is under the
 * pointer and until it has sprung all the way back to flat, at which point its inline values
 * are removed and the theme's own styling is handed back untouched.
 *
 * @type {Map<Element, {content: HTMLElement, current: number[], velocity: number[], target: number[]}>}
 */
const tilting = new Map();

/** @type {Element | null} */
let hovered = null;
let frameId = 0;
let lastTime = 0;

const finePointer = matchMedia('(any-pointer: fine)');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** Mirrors the media query guard in product-card-tilt.liquid. Kept in sync by hand. */
const isEnabled = () => finePointer.matches && !reducedMotion.matches;

/**
 * @param {Element} card
 * @returns {{content: HTMLElement, current: number[], velocity: number[], target: number[]} | null}
 */
function stateFor(card) {
  const existing = tilting.get(card);
  if (existing) return existing;

  const content = card.querySelector(CONTENT_SELECTOR);
  if (!(content instanceof HTMLElement)) return null;

  const state = { content, current: [0, 0, 0], velocity: [0, 0, 0], target: [0, 0, 0] };
  tilting.set(card, state);

  // Gates the CSS rule, so `will-change: transform` is only ever live on a card that is
  // actually moving rather than on every card in the grid.
  if (card instanceof HTMLElement) card.dataset.tiltActive = '';

  return state;
}

/**
 * Points the card at the pointer. The rect read happens here, in the event handler; every
 * style write is deferred to the rAF tick, so reads and writes never interleave.
 *
 * @param {Element} card
 * @param {PointerEvent} event
 */
function aimAt(card, event) {
  const state = stateFor(card);
  if (!state) return;

  const rect = card.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  const xPercent = (event.clientX - rect.left) / rect.width;
  const yPercent = (event.clientY - rect.top) / rect.height;

  state.target[ROTATE_X] = MAX_TILT * (0.5 - yPercent);
  state.target[ROTATE_Y] = MAX_TILT * (xPercent - 0.5);
  state.target[TRANSLATE_Z] = PUSH_Z;

  startLoop();
}

/** @param {Element} card */
function release(card) {
  const state = tilting.get(card);
  if (!state) return;

  state.target[ROTATE_X] = 0;
  state.target[ROTATE_Y] = 0;
  state.target[TRANSLATE_Z] = 0;

  startLoop();
}

/**
 * @param {{current: number[], velocity: number[], target: number[]}} state
 * @param {number} dt seconds
 * @returns {boolean} whether every axis has come to rest
 */
function integrate(state, dt) {
  let settled = true;

  for (let axis = 0; axis < 3; axis++) {
    const offset = state.current[axis] - state.target[axis];
    const velocity = state.velocity[axis];

    if (Math.abs(offset) < REST_OFFSET && Math.abs(velocity) < REST_VELOCITY) {
      state.current[axis] = state.target[axis];
      state.velocity[axis] = 0;
      continue;
    }

    const acceleration = (-STIFFNESS * offset - DAMPING * velocity) / MASS;
    state.velocity[axis] = velocity + acceleration * dt;
    state.current[axis] += state.velocity[axis] * dt;
    settled = false;
  }

  return settled;
}

/**
 * @param {Element} card
 * @param {{content: HTMLElement}} state
 */
function reset(card, state) {
  state.content.style.removeProperty('--tilt-rotate-x');
  state.content.style.removeProperty('--tilt-rotate-y');
  state.content.style.removeProperty('--tilt-translate-z');
  if (card instanceof HTMLElement) delete card.dataset.tiltActive;
}

/** @param {number} now */
function tick(now) {
  const dt = Math.min((now - lastTime) / 1000, MAX_STEP);
  lastTime = now;

  let moving = false;

  for (const [card, state] of tilting) {
    // Facet filtering and the theme editor swap cards out from under us; a detached card
    // would otherwise be sprung forever.
    if (!card.isConnected) {
      tilting.delete(card);
      continue;
    }

    if (integrate(state, dt)) {
      // Settled. A card still under the pointer keeps its state so the next pointermove
      // carries on from where it is; one that has settled back to flat is let go.
      if (card !== hovered) {
        reset(card, state);
        tilting.delete(card);
        continue;
      }
    } else {
      moving = true;
    }

    state.content.style.setProperty('--tilt-rotate-x', `${state.current[ROTATE_X].toFixed(3)}deg`);
    state.content.style.setProperty('--tilt-rotate-y', `${state.current[ROTATE_Y].toFixed(3)}deg`);
    state.content.style.setProperty('--tilt-translate-z', `${state.current[TRANSLATE_Z].toFixed(3)}px`);
  }

  frameId = moving ? requestAnimationFrame(tick) : 0;
}

function startLoop() {
  if (frameId) return;
  lastTime = performance.now();
  frameId = requestAnimationFrame(tick);
}

/** @type {EventTarget | null} */
let lastEventTarget = null;
/** @type {Element | null} */
let lastResolvedCard = null;

/**
 * `closest` per pointermove is cheap, but the pointer spends most of its moves over the same
 * node, so the last answer is memoised.
 *
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function cardFor(target) {
  if (target === lastEventTarget) return lastResolvedCard;

  lastEventTarget = target;
  lastResolvedCard = target instanceof Element ? target.closest(CARD_SELECTOR) : null;

  return lastResolvedCard;
}

document.addEventListener(
  'pointermove',
  (event) => {
    if (event.pointerType === 'touch' || !isEnabled()) return;

    const card = cardFor(event.target);

    if (card !== hovered) {
      if (hovered) release(hovered);
      hovered = card;
    }

    if (card) aimAt(card, event);
  },
  { passive: true }
);

// Fires when the pointer leaves the window entirely, which no pointermove reports.
document.addEventListener('pointerleave', () => {
  if (!hovered) return;
  release(hovered);
  hovered = null;
});

// A tab backgrounded mid-hover never receives a pointerleave, and would restore still tilted.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' && hovered) {
    release(hovered);
    hovered = null;
  }
});
